import { boardDocSchema, demoDoc, type BoardDoc } from '@scribbleplot/shared';

/**
 * The signed-out demo board.
 *
 * Everything lives in the browser: no account, no server writes, nothing to
 * clean up later, and no way for a passing stranger to fill anyone's disk. It
 * is kept in local storage rather than memory so a refresh does not throw away
 * what someone was in the middle of — still nowhere near an account, but not
 * needlessly fragile either.
 */

const KEY = 'sp.demo.doc';

/** Read the saved sandbox, or start from the worked example. */
export function loadSandbox(): BoardDoc {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      // Validate on the way in: this is storage a user can edit by hand, and a
      // malformed document would break the canvas rather than the demo.
      const parsed = boardDocSchema.safeParse(JSON.parse(raw));
      if (parsed.success) return parsed.data;
    }
  } catch {
    // Storage unavailable or corrupt — fall through to a fresh board.
  }
  return demoDoc();
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pending: BoardDoc | null = null;

function write(doc: BoardDoc): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(doc));
  } catch {
    // Quota or private browsing: the demo still works, it just will not survive
    // a reload. Not worth interrupting anyone over.
  }
}

/**
 * Write immediately if anything is waiting.
 *
 * Bound to `pagehide` below: without it, an edit made in the half-second before
 * someone closes the tab would be dropped by the debounce.
 */
export function flushSandbox(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = null;
  if (pending) {
    write(pending);
    pending = null;
  }
}

if (typeof window !== 'undefined') {
  // `pagehide` fires in cases `beforeunload` misses, including the mobile
  // back-forward cache.
  window.addEventListener('pagehide', flushSandbox);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushSandbox();
  });
}

/** Persist the sandbox, debounced — a drag emits a change per pointer frame. */
export function saveSandbox(doc: BoardDoc): void {
  pending = doc;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    if (pending) write(pending);
    pending = null;
  }, 500);
}

export function clearSandbox(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = null;
  // Drop anything queued, or it would immediately rewrite what we just removed.
  pending = null;
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem('sp.cam.demo');
  } catch {
    // Nothing to do.
  }
}

/** Sandbox work parked across sign-up, so it can follow someone into a real account. */
export const CARRY_KEY = 'sp.demo.carry';

export function carrySandbox(doc: BoardDoc): void {
  try {
    sessionStorage.setItem(CARRY_KEY, JSON.stringify(doc));
  } catch {
    // If it cannot be parked, the sign-up still works — the board is just not
    // carried over, and the demo is still in local storage.
  }
}

export function takeCarriedSandbox(): BoardDoc | null {
  try {
    const raw = sessionStorage.getItem(CARRY_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(CARRY_KEY);
    const parsed = boardDocSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
